/**
 * Cannot use a namespace here because Typescript namespaces don't support re-exporting for some reason
 */
import { isServerSide } from 'src/utils/validators/isServerSide';
import type * as RuntimeConfigurationServerSideServiceNamespace from './RuntimeConfigurationService.server';
import { RuntimeConfigurationServiceDomain } from './domain';
export { RuntimeConfigurationServiceDomain as Domain };
export type ClientAvailableEnvironmentVariables = Record<
    RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue,
    string | null
> & {
    isMergeRequest: boolean;
};

export class ConfigProvider extends RuntimeConfigurationServiceDomain.RuntimeConfigProvider {
    private static instance: ConfigProvider;

    // @ts-expect-error - this is initialized in the constructor, but in a method
    private clientAvailableEnvironmentVariables: ClientAvailableEnvironmentVariables;

    private constructor() {
        super();
        this.hydrateClientState();
    }

    public static getInstance() {
        if (!this.instance) {
            this.instance = new ConfigProvider();
        }
        return this.instance;
    }
    public static refreshInstance() {
        const instance = this.getInstance();
        instance.hydrateClientState();
    }

    /**
     * Once we are on Next 15 this can stop being asynchronous since we can just pass it to the client component but sadly for now we have to eat
     * the cost of calling to a service to get this value.
     */
    public getClientAvailableEnvironmentVariable(
        key: RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue
    ) {
        return this.clientAvailableEnvironmentVariables[key];
    }

    public override get deploymentEnvironment(): RuntimeConfigurationServiceDomain.DeploymentEnvironment {
        return this.getClientAvailableEnvironmentVariable(
            RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue.DEPLOYMENT_ENVIRONMENT
        ) as RuntimeConfigurationServiceDomain.DeploymentEnvironment;
    }

    public override get nodeEnvironment(): RuntimeConfigurationServiceDomain.NodeEnvironment {
        return this.getClientAvailableEnvironmentVariable(
            RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue.NODE_ENVIRONMENT
        ) as RuntimeConfigurationServiceDomain.NodeEnvironment;
    }
    public override get isMergeRequest(): boolean {
        return this.clientAvailableEnvironmentVariables.isMergeRequest;
    }

    public get isCurrentRuntimeEnvironmentRedirectExempt(): boolean {
        const REDIRECT_EXEMPTED_ENVIRONMENTS: Partial<RuntimeConfigurationServiceDomain.DeploymentEnvironment>[] = [
            // If we redirect off local, we can't develop locally
            RuntimeConfigurationServiceDomain.DeploymentEnvironment.LOCAL,
            // There is no Zillow domain to redirect to in dev
            RuntimeConfigurationServiceDomain.DeploymentEnvironment.DEVELOPMENT,
        ];
        return REDIRECT_EXEMPTED_ENVIRONMENTS.includes(this.deploymentEnvironment) || this.isMergeRequest;
    }

    private hydrateClientState() {
        /**
         * We have to be able to call this server side (frustratingly, given the domain of this config provider) since NextJS is going to server-side render
         * our client components, and if the config isn't hydrated on the server side, we'll have an SSR hydration error when the components mount and
         * have a different config available.
         */
        if (isServerSide()) {
            /**
             * Requiring it here helps prevent all the issues related to server-side API being automatically called on the client on load which would
             * occur if we imported at the top of the file.
             */
            const ServerConfigProvider = require('./RuntimeConfigurationService.server').ConfigProvider;
            this.clientAvailableEnvironmentVariables = (
                ServerConfigProvider.getInstance() as RuntimeConfigurationServerSideServiceNamespace.ConfigProvider
            ).exportClientSideEnvironmentVariables();
            return;
        }
        this.clientAvailableEnvironmentVariables = Object.values(
            RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue
        ).reduce(
            (acc, value) => ({
                ...acc,
                [value]: window.clientRuntimeConfig.environment[value] ?? null,
            }),
            {
                isMergeRequest: window.clientRuntimeConfig.environment.isMergeRequest,
            } as ClientAvailableEnvironmentVariables
        );
    }
}
